/**
 * DeleteEntityButton — reusable confirm-and-delete trigger.
 *
 * Renders a ghost trash icon button that opens an AlertDialog. While the
 * `onConfirm` async handler is running, the action button shows a spinner
 * and both buttons are disabled. Toasts are the caller's responsibility
 * (we keep this component side-effect-free beyond the actual call).
 */
import { useState } from "react";
import { Trash, CircleNotch } from "@phosphor-icons/react";
import { Button, buttonVariants } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export interface DeleteEntityButtonProps {
  /** Singular pt-BR label, e.g. "roteiro", "decodificação". */
  entityLabel: string;
  /** Optional dialog description override. */
  confirmDescription?: string;
  /** Async handler that performs the actual delete. */
  onConfirm: () => Promise<void>;
  disabled?: boolean;
  size?: "icon" | "sm";
  className?: string;
  "data-testid"?: string;
}

export function DeleteEntityButton({
  entityLabel,
  confirmDescription,
  onConfirm,
  disabled,
  size = "icon",
  className,
  "data-testid": testId,
}: DeleteEntityButtonProps) {
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(false);

  async function handleConfirm(e: React.MouseEvent) {
    e.preventDefault();
    if (pending) return;
    setPending(true);
    try {
      await onConfirm();
      setOpen(false);
    } catch {
      // Caller is responsible for surfacing the error via toast/etc.
      // We just keep the dialog open so the user can retry or cancel.
    } finally {
      setPending(false);
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={(o) => (pending ? null : setOpen(o))}>
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <AlertDialogTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size={size}
                disabled={disabled}
                aria-label={`Excluir ${entityLabel}`}
                onClick={(e) => e.stopPropagation()}
                className={cn(
                  "text-muted-foreground hover:text-destructive hover:bg-destructive/15",
                  className,
                )}
                data-testid={testId}
              >
                <Trash weight="regular" className="w-4 h-4" />
              </Button>
            </AlertDialogTrigger>
          </TooltipTrigger>
          <TooltipContent>Excluir {entityLabel}</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <AlertDialogContent
        onClick={(e) => e.stopPropagation()}
        data-testid={testId ? `${testId}-dialog` : undefined}
      >
        <AlertDialogHeader>
          <AlertDialogTitle>Excluir {entityLabel}?</AlertDialogTitle>
          <AlertDialogDescription>
            {confirmDescription ?? "Esta ação é permanente e não pode ser desfeita."}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={pending}>Cancelar</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={pending}
            className={cn(
              buttonVariants({ variant: "destructive" }),
              "gap-1.5",
            )}
            data-testid={testId ? `${testId}-confirm` : undefined}
          >
            {pending ? (
              <>
                <CircleNotch weight="bold" className="w-4 h-4 animate-spin" />
                Excluindo…
              </>
            ) : (
              "Excluir"
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
